import { Component, EventEmitter, input, Input, output, signal } from '@angular/core';
import { Incident, IncidentLocation } from '../../models/incident';
import { GoogleMap, MapMarker } from '@angular/google-maps';
import { AppModule } from "../../app.module";
import { Router } from '@angular/router';

@Component({
  selector: 'app-map-incidents',
  imports: [AppModule],
  templateUrl: './map-incidents.html',
  styleUrl: './map-incidents.less'
})
export class MapIncidents {

  incidents = input<Incident[]>();
  incidentSelected = output<Incident | null>();

  center: google.maps.LatLngLiteral = { lat: -29.1, lng: 24.2 }; 
  zoom = 6;
  mapOptions: google.maps.MapOptions = {
  disableDefaultUI: true, 
  clickableIcons: false,
  styles: [
    { elementType: "geometry", stylers: [{ color: "#1d2c4d" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#8ec3b9" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#1a3646" }] },
    {
      featureType: "administrative.country",
      elementType: "geometry.stroke",
      stylers: [{ color: "#4b6878" }],
    },
    {
      featureType: "administrative.land_parcel",
      elementType: "labels.text.fill",
      stylers: [{ color: "#64779e" }],
    },
    {
      featureType: "administrative.province",
      elementType: "geometry.stroke",
      stylers: [{ color: "#4b6878" }],
    },
    {
      featureType: "landscape.man_made",
      elementType: "geometry.stroke",
      stylers: [{ color: "#334e87" }],
    },
    {
      featureType: "landscape.natural",
      elementType: "geometry",
      stylers: [{ color: "#023e58" }],
    },
    {
      featureType: "poi",
      elementType: "geometry",
      stylers: [{ color: "#283d6a" }],
    },
    {
      featureType: "poi",
      elementType: "labels.text.fill",
      stylers: [{ color: "#6f9ba5" }],
    },
    {
      featureType: "poi",
      elementType: "labels.text.stroke",
      stylers: [{ color: "#1d2c4d" }],
    },
    {
      featureType: "poi.park",
      elementType: "geometry.fill",
      stylers: [{ color: "#023e58" }],
    },
    {
      featureType: "poi.park",
      elementType: "labels.text.fill",
      stylers: [{ color: "#3C7680" }],
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ color: "#304a7d" }],
    },
    {
      featureType: "road",
      elementType: "labels.text.fill",
      stylers: [{ color: "#98a5be" }],
    },
    {
      featureType: "road",
      elementType: "labels.text.stroke",
      stylers: [{ color: "#1d2c4d" }],
    },
    {
      featureType: "road.highway",
      elementType: "geometry",
      stylers: [{ color: "#2c6675" }],
    },
    {
      featureType: "road.highway",
      elementType: "geometry.stroke",
      stylers: [{ color: "#255763" }],
    },
    {
      featureType: "road.highway",
      elementType: "labels.text.fill",
      stylers: [{ color: "#b0d5ce" }],
    },
    {
      featureType: "road.highway",
      elementType: "labels.text.stroke",
      stylers: [{ color: "#023e58" }],
    },
    {
      featureType: "transit",
      elementType: "labels.text.fill",
      stylers: [{ color: "#98a5be" }],
    },
    {
      featureType: "transit",
      elementType: "labels.text.stroke",
      stylers: [{ color: "#1d2c4d" }],
    },
    {
      featureType: "transit.line",
      elementType: "geometry.fill",
      stylers: [{ color: "#283d6a" }],
    },
    {
      featureType: "transit.station",
      elementType: "geometry",
      stylers: [{ color: "#3a4762" }],
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ color: "#0e1626" }],
    },
    {
      featureType: "water",
      elementType: "labels.text.fill",
      stylers: [{ color: "#4e6d70" }],
    },
  ]
};

constructor(private router: Router) {}

public shouldPulse(incident: Incident): boolean {
  if (incident.statusId === 7) { // 7 = Technician en route
    return incident.originalEta < incident.breakdownTechnicianDriverEta;
  }
  else{
    return false;
  }
  
}

public getPulseIcon(incident: Incident): google.maps.Symbol {
  return {
    path: google.maps.SymbolPath.CIRCLE,
    scale: this.getPulsingScale(incident.incidentId),
    fillColor: '#F44336',  // Red color for overdue incidents
    fillOpacity: 0.3, 
    strokeColor: '#F44336',
    strokeWeight: 2,
    strokeOpacity: 0.5
  };
}

public getIconMarker(incident: Incident): google.maps.Icon {
  const color = this.getStatusColor(incident.statusId);
  return {
    url: '/icons/icon-72x72.png', 
    scaledSize: new google.maps.Size(32, 32),
    anchor: new google.maps.Point(16, 16) 
  };
}

private getPulsingScale(incidentId: number): number {
  const time = Date.now() / 250;
  return 20 + Math.sin(time + incidentId) * 1.5;
}

  private getStatusColor(statusId: number): string {
    const statusColors: { [key: number]: string } = {
      1: '#4CAF50',  // Green - e.g., Completed
      2: '#FFC107',  // Amber - e.g., In Progress
      3: '#F44336',  // Red - e.g., Pending
      4: '#2196F3',  // Blue - e.g., Assigned
      5: '#9C27B0',  // Purple - e.g., On Route
    };
    
    return statusColors[statusId] || '#757575';
  }

  public getMarkerAnimation(incident: Incident): google.maps.Animation | null {
    return this.shouldPulse(incident) ? google.maps.Animation.BOUNCE : null;
  }

  public onMarkerClick(incident: Incident): void {
    this.incidentSelected.emit(incident);
  }

  public getMarkerPosition(incident: Incident): google.maps.LatLngLiteral {
  return {
    lat: Number(incident.location?.latitude) || -29.1,
    lng: Number(incident.location?.longitude) || 24.2
  };
}
}